// @ts-strict-ignore
import Button from 'antd/lib/button';
import Divider from 'antd/lib/divider';
import List from 'antd/lib/list';
import Text from 'antd/lib/typography/Text';
import Timeline from 'antd/lib/timeline';
import * as React from 'react';
import { formatDateWithTimezone } from '../../timeHelpers';
import { dayjs } from '@/extendedDayjs';
import TimeWithConditionalTz from '../common/TimeWithConditionalTz';
import { BuildDistroWorkItemsByBuild, FeatureItem, PRItem } from '../../types';
import { AppComponentProps } from '../../typescriptHelpers';
import { antGrey, frRowIgnoredStyle, runwayColors } from '../styles/globalStyles';
import { BuildNumberTagBase } from './CommonComponents';
import { codeIsCommit } from '../../helpers';
import CodeStatus from '../featureReadiness/CodeStatus';
import TicketStatus from '../featureReadiness/TicketStatus';
import {
    selectAppTimeFormatSetting,
    selectEffectiveTimezone,
    selectFeatureManagementIntegration,
    selectVCSIntegration,
} from '../../selectors/selectors';
import { RootState } from '../../reducers/reducers';
import { useSelector } from 'react-redux';
import { useIsMobile } from '../../mediaQueriesHelpers';
import {
    renderIcons,
    renderItemDesc,
    renderTicketOwner,
    renderCodeOwner,
} from '../featureReadiness/FeatureReadinessItem';

export interface WorkItemsDiffTimelineProps extends AppComponentProps {
    workItemsByBuild?: BuildDistroWorkItemsByBuild[];
    onLoadMore?: () => void;
    shouldShowLoadMore?: boolean;
    isLoading?: boolean;
    error?: string;
    isAutoMergeRunwayPRsEnabled: boolean;
}

export const WorkItemsDiffTimeline: React.FC<WorkItemsDiffTimelineProps> = ({
    appId,
    orgId,
    workItemsByBuild,
    onLoadMore,
    shouldShowLoadMore,
    isLoading,
    error,
    isAutoMergeRunwayPRsEnabled,
}) => {
    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, appId));
    const effectiveTimezone = useSelector((state: RootState) => selectEffectiveTimezone(state, appId));

    return (
        <List
            loadMore={
                onLoadMore &&
                shouldShowLoadMore && (
                    <div
                        style={{
                            textAlign: 'center',
                            marginTop: 12,
                            height: 32,
                            lineHeight: '32px',
                        }}
                    >
                        <Button type="link" onClick={onLoadMore}>
                            Load more...
                        </Button>
                    </div>
                )
            }
            locale={{
                emptyText: error ?? 'No changes.',
            }}
            loading={isLoading}
            dataSource={workItemsByBuild}
            split={false}
            renderItem={(buildGroup, index) => (
                <List.Item
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                    }}
                >
                    {index !== 0 && (
                        <div
                            style={{
                                display: 'flex',
                                gap: 16,
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                width: '100%',
                            }}
                        >
                            <Divider
                                type="horizontal"
                                dashed
                                style={{
                                    width: 'unset',
                                    minWidth: 'unset',
                                    flexGrow: 1,
                                    borderColor: antGrey.grey5,
                                }}
                            />
                            <div
                                style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    justifyContent: 'flex-end',
                                    alignItems: 'center',
                                    gap: 8,
                                    padding: '12px 0',
                                }}
                            >
                                <Text
                                    style={{
                                        color: antGrey.grey8,
                                        padding: '0 8px',
                                    }}
                                    ellipsis
                                >
                                    <TimeWithConditionalTz
                                        triggeredAtLabel={formatDateWithTimezone(
                                            buildGroup.uploadedAt,
                                            'short',
                                            effectiveTimezone,
                                            use24HourTimeFormat,
                                        )}
                                        triggeredByMoment={dayjs(buildGroup.uploadedAt)}
                                        use24HourTimeFormat={use24HourTimeFormat}
                                        effectiveTimezone={effectiveTimezone}
                                        style={{ color: 'inherit' }}
                                    />
                                </Text>
                                <BuildNumberTagBase {...buildGroup} backgroundColor={'transparent'} />
                            </div>
                        </div>
                    )}
                    {buildGroup.items.length === 0 ?
                        <div
                            style={{
                                padding: 16,
                                paddingBottom: 4,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                width: '100%',
                            }}
                        >
                            <Text
                                style={{
                                    color: antGrey.grey5,
                                }}
                            >
                                No new work items
                            </Text>
                        </div>
                    :   <Timeline
                            style={{
                                width: '100%',
                            }}
                            className="work-items-timeline"
                        >
                            {buildGroup.items.map((item) => (
                                <Timeline.Item key={item.id} color={runwayColors.statusGreen}>
                                    <WorkItemCard
                                        appId={appId}
                                        orgId={orgId}
                                        {...item}
                                        isAutoMergeRunwayPRsEnabled={isAutoMergeRunwayPRsEnabled}
                                    />
                                </Timeline.Item>
                            ))}
                        </Timeline>
                    }
                </List.Item>
            )}
        />
    );
};

export interface WorkItemCardProps extends FeatureItem, AppComponentProps {
    extraComponent?: React.ReactNode;
    isAutoMergeRunwayPRsEnabled?: boolean;
}

const WorkItemCard: React.FC<WorkItemCardProps> = (props) => {
    const itemHasIssues = props.associatedStabilityIssues?.length > 0;

    const isMobile = useIsMobile();

    const featureManagementIntegration = useSelector((state: RootState) =>
        selectFeatureManagementIntegration(state, props.appId),
    );
    const vcsIntegration = useSelector((state: RootState) => selectVCSIntegration(state, props.appId));

    const featureManagementIntegrationType = featureManagementIntegration?.id;
    const vcsIntegrationType = vcsIntegration?.id;

    const baseContainerStyle: React.CSSProperties = {
        flexGrow: 1,
        display: 'flex',
        alignItems: 'center',
        paddingTop: 10,
        paddingBottom: 10,
        minWidth: 0,
    };

    const baseContainerStyles: React.CSSProperties = {
        display: 'flex',
        alignItems: 'stretch',
        flexGrow: 1,
        minWidth: 0,
        cursor: 'pointer',
        paddingLeft: 8,
    };

    const backgroundStyles: React.CSSProperties = { backgroundColor: antGrey.grey2, borderRadius: 8 };

    return (
        <div
            style={
                itemHasIssues ?
                    {
                        borderLeft: `2px solid ${runwayColors.statusRed}`,
                        marginTop: 4,
                        ...baseContainerStyles,
                        ...backgroundStyles,
                    }
                :   { ...baseContainerStyles, ...backgroundStyles }
            }
            onClick={(e) => {
                e.stopPropagation();
            }}
        >
            <div style={props.ignored ? { ...frRowIgnoredStyle, ...baseContainerStyle } : baseContainerStyle}>
                {renderIcons(
                    featureManagementIntegrationType,
                    vcsIntegrationType,
                    !!props.ticket,
                    !!props.code,
                    props.ignored,
                    props.explanationText,
                )}
                <div
                    style={{
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        marginLeft: 20,
                        minWidth: 0,
                    }}
                >
                    <div style={{ display: 'flex', minHeight: 60, alignItems: props.ticket ? 'flex-end' : 'center' }}>
                        <div
                            style={{
                                display: 'flex',
                                flexGrow: 1,
                                minWidth: 0,
                                flexDirection: 'column',
                                gap: 8,
                            }}
                        >
                            <div style={{ width: 280, flexShrink: 0 }}>
                                <TicketStatus ticket={props.ticket} ignored={props.ignored} />
                            </div>
                            <div style={{ flexGrow: 1, minWidth: 0 }}>
                                {renderItemDesc(
                                    props.ticket?.description,
                                    props.ticket?.featureAffiliations || [],
                                    false,
                                    props.ticket?.additionalFields,
                                    {
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'stretch',
                                        justifyContent: 'center',
                                        paddingRight: 12,
                                    },
                                )}
                            </div>
                        </div>
                        {!isMobile &&
                            renderTicketOwner(!!props.ticket, props.ticket?.owner, false, {
                                display: 'flex',
                                alignItems: 'center',
                                alignSelf: 'center',
                            })}
                    </div>
                    <Divider dashed style={{ margin: '10px 0px', borderColor: antGrey.grey6 }} />
                    <div style={{ display: 'flex', minHeight: 60, alignItems: props.code ? 'flex-start' : 'center' }}>
                        <div
                            style={{
                                display: 'flex',
                                flexGrow: 1,
                                minWidth: 0,
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                gap: 8,
                            }}
                        >
                            <div style={{ width: 280, flexShrink: 0 }}>
                                <CodeStatus
                                    code={props.code}
                                    appId={props.appId}
                                    ignored={props.ignored}
                                    workingBranchEnabled={false}
                                    isAutoMergeRunwayPRsEnabled={props.isAutoMergeRunwayPRsEnabled}
                                    automationTooltipMessage={props.automationTooltipMessage}
                                />
                            </div>
                            <div style={{ flexGrow: 1, minWidth: 0 }}>
                                {renderItemDesc(
                                    props.code ?
                                        codeIsCommit(props.code) ? props.code.message
                                        :   (props.code as PRItem).title
                                    :   null,
                                    [],
                                    props.ignored,
                                )}
                            </div>
                        </div>
                        {!isMobile && renderCodeOwner(props.code, false)}
                    </div>
                </div>
            </div>
            {props.extraComponent}
        </div>
    );
};
